import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AppState } from '@src/providers/AppStateProvider/types';
import { buildUrlWithParams } from '@src/utils/rtkQuery/buildUrlWithParams';
import type { CoinMetricsDataResponse, CoinMetricsError, CoinMetricsQueryArgs } from './coinMetrics.types';
import {
  COIN_METRICS_COMMUNITY_BASE_URL,
  COIN_METRICS_DEFAULT_MAX_PAGES,
  COIN_METRICS_PRO_BASE_URL,
  COIN_METRICS_TIER,
} from './coinMetricsConstants';
import { selectCoinMetricsApiKey } from './coinMetricsCredentialsSlice';

function isValidUrl(s?: string): s is string {
  if (!s) {
    return false;
  }
  try {
    new URL(s);
    return true;
  } catch {
    return false;
  }
}

/**
 * Fetches a single page from the CoinMetrics API.
 * All auth and credential concerns are handled by the caller before calling this.
 */
async function fetchOnePage<T>(
  url: string
): Promise<{ data: CoinMetricsDataResponse<T> } | { error: CoinMetricsError }> {
  try {
    const response = await fetch(url, { method: 'GET', credentials: 'omit' });

    if (!response.ok) {
      // CM error responses are JSON: { "error": { "type": "...", "message": "..." } }
      // Fall back to raw text if parsing fails (e.g. gateway-level HTML errors).
      let message: string;
      let type: string | undefined;
      try {
        const body = await response.json();
        message = body?.error?.message ?? JSON.stringify(body);
        type = body?.error?.type;
      } catch {
        message = await response.text().catch(() => `HTTP ${response.status}`);
      }
      return { error: { status: response.status, message, type } satisfies CoinMetricsError };
    }

    if (response.status === 204) {
      return { data: { data: [] } };
    }

    try {
      return { data: (await response.json()) as CoinMetricsDataResponse<T> };
    } catch {
      // Preserve the real HTTP status — a parse failure here is not a network error.
      // Common causes: gateway returning HTML with a 200, or an unexpected content-type.
      return {
        error: {
          status: response.status,
          message: `Received a non-JSON success response (HTTP ${response.status})`,
        } satisfies CoinMetricsError,
      };
    }
  } catch (error) {
    return {
      error: {
        status: 0,
        message: error instanceof Error ? error.message : 'Unknown network error',
      } satisfies CoinMetricsError,
    };
  }
}

/**
 * Custom RTK Query base query for the CoinMetrics external API.
 *
 * Key invariants:
 * - Uses raw `fetch`, never `kyoko/http.ts` — no Talos cookies or headers must leak to CoinMetrics.
 * - `credentials: 'omit'` ensures browser never sends Talos session cookies cross-origin.
 * - API key is appended as a URL query param (`?api_key=...`) for pro tier only.
 * - 401 responses are returned as errors and do NOT touch the Talos auth session.
 * - Automatically follows `next_page_url` when `next_page_token` is present, merging all
 *   pages into a single response before returning. No opt-in flag required.
 */
export const coinMetricsBaseQuery: BaseQueryFn<CoinMetricsQueryArgs, unknown, CoinMetricsError> = async (
  { url, params, tier, maxPages },
  api
) => {
  const apiKey = selectCoinMetricsApiKey(api.getState() as AppState);
  const baseUrl = tier === COIN_METRICS_TIER.PRO ? COIN_METRICS_PRO_BASE_URL : COIN_METRICS_COMMUNITY_BASE_URL;

  if (tier === COIN_METRICS_TIER.PRO && !apiKey) {
    return {
      error: {
        status: 0,
        message:
          'Pro API key is not configured for this organization. Please check ui.CoinMetricsApiKey in the organization config.',
      } satisfies CoinMetricsError,
    };
  }

  const authParams = tier === COIN_METRICS_TIER.PRO && apiKey ? { api_key: apiKey } : undefined;
  const firstPageUrl = buildUrlWithParams(`${baseUrl}${url}`, { ...params, ...authParams });

  const cap = maxPages ?? COIN_METRICS_DEFAULT_MAX_PAGES;
  const allData: unknown[] = [];
  let nextUrl: string | undefined = firstPageUrl;
  let pagesFetched = 0;
  // True once we stop at the cap with more pages still available — `allData` is then
  // an incomplete prefix of the full result. Surfaced so aggregating/charting callers
  // can warn instead of silently rendering partial data.
  let truncated = false;

  while (nextUrl) {
    if (pagesFetched >= cap) {
      console.warn(
        `Auto-pagination stopped after ${cap} pages for ${url}. ` +
          `${allData.length} records returned. Narrow the query (start_time/end_time, page_size) or increase maxPages.`
      );
      truncated = true;
      break;
    }

    const result: { data: CoinMetricsDataResponse<unknown> } | { error: CoinMetricsError } =
      await fetchOnePage<unknown>(nextUrl);

    if ('error' in result) {
      return result;
    }

    allData.push(...result.data.data);
    pagesFetched++;

    // CM response fields are snake_case by API convention — access via index to avoid camelcase lint rule.
    const nextPageToken: string | undefined = result.data['next_page_token'];
    const nextPageUrl: string | undefined = result.data['next_page_url'];
    const hasNextPage: boolean = !!nextPageToken && isValidUrl(nextPageUrl);
    // CM includes all initial params (including api_key) in next_page_url — use it as-is.
    nextUrl = hasNextPage ? nextPageUrl : undefined;
  }

  // Only attach `truncated` when true — keeps the common (complete) response shape clean
  // and lets consumers treat its absence as "not truncated".
  return { data: { data: allData, ...(truncated && { truncated: true }) } };
};
